/**
 * Shared client/server coerce helpers for compose archetype extract.
 * Keep free of `@/` aliases so vercel/node typecheck can import it.
 */

export type ComposeArchetypeSlotDef = {
  key: string;
  label: string;
  description: string;
  seriesSpecific: boolean;
  sizeSpecific: boolean;
  styleSpecific: boolean;
  changesWithSize: boolean;
  thresholdSize: string;
  aboveDescription: string;
};

export type ComposeArchetypeDraft = {
  seriesName: string;
  seriesCode: string;
  namingPattern: string;
  sizes: string[];
  styles: string[];
  slots: ComposeArchetypeSlotDef[];
  skuList: Array<{ code: string; name: string }>;
  notes: string | null;
};

const SIZE_RE = /(\d+)\s*[x×]\s*(\d+)/i;

export function normalizeComposeSizeLabel(raw: string): string | null {
  const m = raw.trim().match(SIZE_RE);
  if (!m) return null;
  return `${m[1]}x${m[2]}`;
}

export function parseComposeSizeList(raw: string): string[] {
  const parts = raw.split(/[,;\n]+/).map((p) => p.trim()).filter(Boolean);
  const out: string[] = [];
  const seen = new Set<string>();
  for (const part of parts) {
    const size = normalizeComposeSizeLabel(part);
    if (!size || seen.has(size)) continue;
    seen.add(size);
    out.push(size);
  }
  return out;
}

export function parseComposeStylesRaw(raw: string): string[] {
  return raw
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function emptyComposeArchetypeSlot(
  partial?: Partial<ComposeArchetypeSlotDef>,
): ComposeArchetypeSlotDef {
  return {
    key: "",
    label: "",
    description: "",
    seriesSpecific: false,
    sizeSpecific: false,
    styleSpecific: false,
    changesWithSize: false,
    thresholdSize: "",
    aboveDescription: "",
    ...partial,
  };
}

/** One blank starter row — not industry-specific; no flags checked. */
export const DEFAULT_COMPOSE_ARCHETYPE_SLOTS: ComposeArchetypeSlotDef[] = [
  emptyComposeArchetypeSlot({
    key: "floor",
    label: "Floor",
    description: "floor",
  }),
];

function coerceSlot(item: unknown): ComposeArchetypeSlotDef | null {
  if (!item || typeof item !== "object") return null;
  const s = item as Record<string, unknown>;
  const key = typeof s.key === "string" ? s.key.trim() : "";
  if (!key) return null;
  const label =
    typeof s.label === "string" && s.label.trim() ? s.label.trim() : key;

  const styleSpecific =
    s.styleSpecific === true || s.splitByStyle === true || s.style_specific === true;
  const seriesSpecific = s.seriesSpecific === true || s.series_specific === true;
  const changesWithSize =
    s.changesWithSize === true ||
    s.changes_with_size === true ||
    s.variantFromPredicate === "door" ||
    s.variantFromPredicate === "wall_height";

  let sizeSpecific = false;
  if ("sizeSpecific" in s || "size_specific" in s) {
    sizeSpecific = Boolean(s.sizeSpecific ?? s.size_specific);
  } else if ("sharedBySize" in s) {
    sizeSpecific = s.sharedBySize !== false;
  }

  return emptyComposeArchetypeSlot({
    key,
    label,
    description: typeof s.description === "string" ? s.description.trim() : "",
    seriesSpecific,
    sizeSpecific,
    styleSpecific,
    changesWithSize,
    thresholdSize:
      typeof s.thresholdSize === "string"
        ? s.thresholdSize.trim()
        : typeof s.threshold_size === "string"
          ? s.threshold_size.trim()
          : "",
    aboveDescription:
      typeof s.aboveDescription === "string"
        ? s.aboveDescription.trim()
        : typeof s.above_description === "string"
          ? s.above_description.trim()
          : "",
  });
}

/**
 * When the model still returns a legacy global predicate, fold door/wall
 * rules into matching slots.
 */
function applyLegacyPredicateToSlots(
  slots: ComposeArchetypeSlotDef[],
  predicate: Record<string, unknown> | null,
): ComposeArchetypeSlotDef[] {
  if (!predicate) return slots;
  const threshold =
    typeof predicate.thresholdSize === "string"
      ? predicate.thresholdSize.trim()
      : "";
  const belowDoor = predicate.belowDoor === "double" ? "double" : "single";
  const aboveDoor = predicate.aboveDoor === "single" ? "single" : "double";
  const belowWall = predicate.belowWallHeight === "7ft" ? "7ft" : "6ft";
  const aboveWall = predicate.aboveWallHeight === "6ft" ? "6ft" : "7ft";

  return slots.map((slot) => {
    if (slot.key === "door" && !slot.changesWithSize && !slot.description) {
      return {
        ...slot,
        description: `${belowDoor} wooden shed door`,
        aboveDescription: `${aboveDoor} wooden shed door`,
        changesWithSize: true,
        thresholdSize: threshold || slot.thresholdSize,
        sizeSpecific: false,
      };
    }
    if (slot.key === "walls" && !slot.changesWithSize && !slot.description) {
      return {
        ...slot,
        description: `${belowWall} walls`,
        aboveDescription: `${aboveWall} walls`,
        changesWithSize: true,
        thresholdSize: threshold || slot.thresholdSize,
        sizeSpecific: true,
      };
    }
    return slot;
  });
}

export function coerceComposeArchetypeDraft(raw: unknown): ComposeArchetypeDraft | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;

  const seriesName = typeof row.seriesName === "string" ? row.seriesName.trim() : "";
  const seriesCode = typeof row.seriesCode === "string" ? row.seriesCode.trim() : "";
  const namingPattern =
    typeof row.namingPattern === "string" && row.namingPattern.trim()
      ? row.namingPattern.trim()
      : "{CODE}-{SIZE}-{STYLE}";

  const sizesRaw = Array.isArray(row.sizes) ? row.sizes : [];
  const sizes: string[] = [];
  for (const s of sizesRaw) {
    if (typeof s !== "string") continue;
    const n = normalizeComposeSizeLabel(s);
    if (n) sizes.push(n);
  }

  const stylesRaw = Array.isArray(row.styles) ? row.styles : [];
  const styles = stylesRaw
    .filter((s: unknown): s is string => typeof s === "string")
    .map((s: string) => s.trim())
    .filter(Boolean);

  let slots: ComposeArchetypeSlotDef[] = DEFAULT_COMPOSE_ARCHETYPE_SLOTS.map((s) => ({
    ...s,
  }));
  if (Array.isArray(row.slots) && row.slots.length > 0) {
    const parsed: ComposeArchetypeSlotDef[] = [];
    for (const item of row.slots) {
      const slot = coerceSlot(item);
      if (slot) parsed.push(slot);
    }
    if (parsed.length > 0) slots = parsed;
  }

  const legacyPred =
    row.predicate && typeof row.predicate === "object"
      ? (row.predicate as Record<string, unknown>)
      : null;
  slots = applyLegacyPredicateToSlots(slots, legacyPred);

  const skuList: Array<{ code: string; name: string }> = [];
  if (Array.isArray(row.skuList)) {
    for (const item of row.skuList) {
      if (!item || typeof item !== "object") continue;
      const s = item as Record<string, unknown>;
      const code = typeof s.code === "string" ? s.code.trim() : "";
      const name = typeof s.name === "string" ? s.name.trim() : code;
      if (!code) continue;
      skuList.push({ code, name: name || code });
    }
  }

  const notes = typeof row.notes === "string" ? row.notes.trim() || null : null;

  return {
    seriesName,
    seriesCode,
    namingPattern,
    sizes,
    styles,
    slots,
    skuList,
    notes,
  };
}

/** Heuristic fallback when OpenAI is unavailable. */
export function heuristicComposeArchetypeDraft(transcript: string): ComposeArchetypeDraft | null {
  const sizes = parseComposeSizeList(transcript);
  const styles: string[] = [];
  for (const s of ["Utility", "Lofted", "Studio"]) {
    if (new RegExp(s, "i").test(transcript)) styles.push(s);
  }
  const seriesMatch = transcript.match(/\b(classic|greenhouse|lofted\s+barn|utility)\b/i);
  const seriesName = seriesMatch?.[1]
    ? seriesMatch[1].replace(/\b\w/g, (c) => c.toUpperCase())
    : "";
  const seriesCode = seriesName
    ? seriesName
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, "-")
        .slice(0, 10)
    : "";

  const skuLines = transcript
    .split(/\n+/)
    .map((l) => l.trim())
    .filter((l) => /^[A-Z0-9][A-Z0-9._-]{1,}/i.test(l) && /x\d/i.test(l));
  const skuList = skuLines.map((line) => {
    const pipe = line.indexOf("|");
    const code = (pipe >= 0 ? line.slice(0, pipe) : line.split(/\s+/)[0] ?? line).trim();
    const name = (pipe >= 0 ? line.slice(pipe + 1) : code).trim();
    return { code, name: name || code };
  });

  if (!sizes.length && !styles.length && !skuList.length && !seriesName) {
    if (!/style|size|series|group|sku|product/i.test(transcript)) return null;
  }

  return {
    seriesName,
    seriesCode,
    namingPattern: "{CODE}-{SIZE}-{STYLE}",
    sizes,
    styles,
    slots: DEFAULT_COMPOSE_ARCHETYPE_SLOTS.map((s) => ({ ...s })),
    skuList,
    notes: null,
  };
}
